/**
 * Sender Label Rules API Client
 * Manages learned label rules from user corrections
 */

import { getStoredSessionId } from './auth-client';

const CF_WORKER_URL = import.meta.env.VITE_CLOUDFLARE_WORKER_URL || '';
const API_BASE = CF_WORKER_URL ? `${CF_WORKER_URL}/api` : '/api';

export interface SenderLabelRule {
  id: string;
  user_id: string;
  sender_email: string;
  sender_domain: string | null;
  label_name: string;
  source: 'gmail_learned' | 'user_correction';
  created_from_email_id: string | null;
  times_applied: number;
  created_at: number;
  updated_at: number;
  // Evidence fields for explainability
  evidence_email_count: number | null;
  evidence_consistency: number | null;
  evidence_type: 'sender_pattern' | 'domain_pattern' | 'gmail_filter' | 'user_correction' | null;
  evidence_description: string | null;
  evidence_sample_subjects: string | null;
}

export interface CreateSenderLabelRuleInput {
  senderEmail: string;
  labelName: string;
  createdFromEmailId?: string;
}

function getAuthHeaders(): HeadersInit {
  const sessionId = getStoredSessionId();
  return sessionId ? { 'X-Session-Id': sessionId } : {};
}

/**
 * Fetch all sender label rules for the current user
 */
export async function fetchSenderLabelRules(): Promise<SenderLabelRule[]> {
  const response = await fetch(`${API_BASE}/sender-label-rules`, {
    credentials: 'include',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch sender label rules');
  }

  const data = await response.json();
  return data.data.rules || [];
}

/**
 * Create or update a sender label rule
 * If a rule already exists for this sender, it will be updated
 */
export async function createSenderLabelRule(input: CreateSenderLabelRuleInput): Promise<{ rule: SenderLabelRule; created: boolean; updated: boolean }> {
  const response = await fetch(`${API_BASE}/sender-label-rules`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || 'Failed to create sender label rule');
  }

  const data = await response.json();
  return {
    rule: data.data.rule,
    created: data.data.created || false,
    updated: data.data.updated || false,
  };
}

/**
 * Update a sender label rule
 */
export async function updateSenderLabelRule(id: string, labelName: string): Promise<SenderLabelRule> {
  const response = await fetch(`${API_BASE}/sender-label-rules/${id}`, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify({ labelName }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || 'Failed to update sender label rule');
  }

  const data = await response.json();
  return data.data.rule;
}

/**
 * Delete a sender label rule
 */
export async function deleteSenderLabelRule(id: string): Promise<void> {
  const response = await fetch(`${API_BASE}/sender-label-rules/${id}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || 'Failed to delete sender label rule');
  }
}
